<template>
  <v-dialog :value="value" :max-width="step === 'intro' ? 620 : 580" :persistent="busy" @input="!$event && close()">
    <!-- Input de archivo oculto: lo abre "Seleccionar archivo" -->
    <input ref="fileInput" type="file" accept=".xlsx,.xls,.csv" class="hidden-input" @change="onFileSelected" />

    <v-card>
      <!-- ── PASO 1: qué hace la carga y qué archivo espera ── -->
      <template v-if="step === 'intro'">
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left color="primary">mdi-cash-multiple</v-icon>
          Cargar pagos
        </v-card-title>
        <v-card-text>
          <p class="mb-3 body-copy">
            Sube el archivo con los <strong>pagos</strong> hechos a las clínicas y las <strong>notas de crédito</strong>
            recibidas para actualizar el estado de sus facturas.
          </p>

          <div class="text-subtitle-2 font-weight-bold mb-1">El archivo debe tener</div>
          <ul class="mb-2 requirements">
            <li>Formato <code>.xlsx</code>, <code>.xls</code> o <code>.csv</code>, con cabeceras.</li>
            <li>
              Una columna <strong>RUT clínica</strong> (con o sin puntos y guion: <code>76.482.119-3</code> o
              <code>764821193</code>) y una <strong>N° de Factura</strong> (el folio).
            </li>
            <li>
              Una columna <strong>Estado</strong>: <code>Pagado</code> o <code>Nota de Crédito Subida</code> (en la
              plantilla se elige de una lista).
            </li>
            <li>
              Una columna <strong>Fecha de pago</strong> (<code>DD/MM/AAAA</code>, <code>DD-MM-AAAA</code>,
              <code>DD.MM.AAAA</code> o <code>AAAA-MM-DD</code>), obligatoria para <code>Pagado</code>.
            </li>
          </ul>
          <v-btn text small color="primary" class="template-btn mb-3" @click="downloadTemplate">
            <v-icon small left>mdi-download</v-icon>Descargar plantilla (.xlsx)
          </v-btn>

          <v-alert type="warning" dense outlined class="mb-2" icon="mdi-file-cancel-outline">
            <div class="font-weight-bold mb-1">Solo se actualizan las facturas que corresponden</div>
            <div class="small-copy">
              <strong>Pagado</strong> aplica a facturas en Pendiente de Pago o Requiere NC, y
              <strong>Nota de Crédito Subida</strong> a las que están en Requiere NC. Las Anuladas, las Sin Facturar
              y las ya pagadas no cambian: se informan al final para que las revises desde su detalle.
            </div>
          </v-alert>

          <div class="footnote">
            Las filas que no se puedan aplicar se informan aparte, sin tocar nada. Antes de escribir nada vas a ver un
            resumen para confirmar.
          </div>

          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">{{ errorMessage }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="busy" @click="close">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="reading" @click="chooseFile">
            <v-icon left>mdi-upload</v-icon>Seleccionar archivo
          </v-btn>
        </v-card-actions>
      </template>

      <!-- ── PASO 2: resumen para confirmar ── -->
      <template v-else-if="step === 'confirm'">
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left color="primary">mdi-cash-multiple</v-icon>
          Confirmar carga de pagos
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Se detectaron <strong>{{ rows.length }}</strong> {{ rows.length === 1 ? 'fila' : 'filas' }}:
            <strong>{{ summary.paymentCount }}</strong> {{ summary.paymentCount === 1 ? 'pago' : 'pagos' }} en
            <strong>{{ summary.payments.length }}</strong> fecha(s) y
            <strong>{{ summary.creditNotes.length }}</strong>
            {{ summary.creditNotes.length === 1 ? 'nota de crédito' : 'notas de crédito' }}.
          </p>
          <v-simple-table v-if="summary.payments.length || summary.creditNotes.length" dense class="mb-2">
            <thead>
              <tr>
                <th>Movimiento</th>
                <th class="text-center">Facturas</th>
                <th>Folios</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="group in summary.payments" :key="group.date">
                <td>Pago · {{ group.date }}</td>
                <td class="text-center">{{ group.invoices.length }}</td>
                <td class="folios">{{ folioList(group.invoices) }}</td>
              </tr>
              <tr v-if="summary.creditNotes.length">
                <td>Nota de crédito</td>
                <td class="text-center">{{ summary.creditNotes.length }}</td>
                <td class="folios">{{ folioList(summary.creditNotes) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-alert v-if="summary.unknown.length" type="info" dense outlined class="mb-0">
            {{ summary.unknown.length }}
            {{ summary.unknown.length === 1 ? 'fila tiene' : 'filas tienen' }} un estado no reconocido (filas
            {{ summary.unknown.join(', ') }}): se informarán al final sin aplicarse.
          </v-alert>
          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">{{ errorMessage }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="busy" @click="backToIntro">Volver</v-btn>
          <v-btn color="primary" depressed :loading="submitting" @click="confirm">Confirmar</v-btn>
        </v-card-actions>
      </template>

      <!-- ── PASO 3: resultado ── -->
      <template v-else>
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left color="success">mdi-check-circle-outline</v-icon>
          Resultado de la carga
        </v-card-title>
        <v-card-text>
          <v-alert :type="result.applied ? 'success' : 'info'" dense outlined class="mb-3" icon="mdi-cash-check">
            {{ appliedMessage }}
          </v-alert>
          <v-alert
            v-for="group in rejectedGroups"
            :key="group.reason"
            type="warning"
            dense
            outlined
            class="mb-2"
            icon="mdi-alert-circle-outline"
          >
            <div class="font-weight-bold mb-1">{{ group.reason }} ({{ group.items.length }})</div>
            <div class="small-copy">{{ group.items.join(', ') }}</div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="close">Cerrar</v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import ClinicSettlementsService from '@/services/clinic-settlements.service';
import {
  PAYMENTS_MAX_FILE_BYTES,
  PAYMENTS_MAX_ROWS,
  downloadPaymentsTemplate,
  folioList,
  groupRejectedRows,
  readPaymentsFile,
  settlementErrorMessage,
  summarizePaymentRows
} from '@/utils/clinic-settlements.utils';

const ACCEPTED_FILE = /\.(xlsx|xls|csv)$/i;

// "Cargar pagos" (125 US5), con el mismo flujo que la carga de pagos de reembolsos:
// 1) explica el archivo y abre el selector, 2) muestra un resumen para confirmar sin
// escribir nada, 3) aplica las filas y muestra el resultado agrupado por motivo.
// Emite `applied` cuando el backend procesó el archivo, para que la vista recargue la tabla.
export default {
  name: 'LoadPaymentsDialog',
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return { step: 'intro', file: null, rows: [], reading: false, submitting: false, errorMessage: '', result: null };
  },
  computed: {
    busy() {
      return this.reading || this.submitting;
    },
    summary() {
      return summarizePaymentRows(this.rows);
    },
    rejectedGroups() {
      return this.result ? groupRejectedRows(this.result.rejected, this.rows) : [];
    },
    appliedMessage() {
      const applied = this.result.applied;
      if (!applied) return 'No se actualizó ninguna factura.';
      return applied === 1 ? '1 factura actualizada.' : `${applied} facturas actualizadas.`;
    }
  },
  watch: {
    value(open) {
      if (open) this.reset();
    }
  },
  methods: {
    folioList,
    reset() {
      this.step = 'intro';
      this.file = null;
      this.rows = [];
      this.reading = false;
      this.submitting = false;
      this.errorMessage = '';
      this.result = null;
    },
    close() {
      if (this.busy) return;
      this.$emit('input', false);
    },
    async downloadTemplate() {
      try {
        await downloadPaymentsTemplate();
      } catch (error) {
        this.errorMessage = 'No se pudo generar la plantilla. Intenta nuevamente.';
      }
    },
    chooseFile() {
      const input = this.$refs.fileInput;
      input.value = '';
      input.click();
    },
    backToIntro() {
      this.step = 'intro';
      this.errorMessage = '';
    },
    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.readFile(file);
    },
    validateFile(file) {
      if (!ACCEPTED_FILE.test(file.name)) return 'El archivo debe ser .xlsx, .xls o .csv.';
      if (file.size > PAYMENTS_MAX_FILE_BYTES) return 'El archivo supera los 5 MB.';
      return '';
    },
    async parseFile(file) {
      try {
        return await readPaymentsFile(file);
      } catch (error) {
        return { rows: [], missingColumns: [], unreadable: true };
      }
    },
    rowsError(parsed) {
      if (parsed.unreadable) return 'No se pudo leer el archivo. Revisa que sea un .xlsx, .xls o .csv válido.';
      if (parsed.missingColumns.length) return `Faltan columnas en el archivo: ${parsed.missingColumns.join(', ')}.`;
      if (!parsed.rows.length) return 'El archivo no tiene filas con pagos.';
      if (parsed.rows.length > PAYMENTS_MAX_ROWS) return `El archivo tiene más de ${PAYMENTS_MAX_ROWS} filas.`;
      return '';
    },
    // Paso 1 → 2: valida y lee el archivo en el navegador; si algo falla, se queda en el paso 1.
    async readFile(file) {
      this.file = file;
      this.errorMessage = this.validateFile(file);
      if (this.errorMessage) return;

      this.reading = true;
      const parsed = await this.parseFile(file);
      this.reading = false;
      this.errorMessage = this.rowsError(parsed);
      if (this.errorMessage) return;

      this.rows = parsed.rows;
      this.step = 'confirm';
    },
    // Paso 2 → 3: recién aquí se escribe.
    async confirm() {
      this.submitting = true;
      this.errorMessage = '';
      const res = await ClinicSettlementsService.applyPayments(this.rows);
      this.submitting = false;
      if (res.status !== 'ok') {
        this.errorMessage = settlementErrorMessage(res, 'No se pudo procesar el archivo. Intenta nuevamente.');
        return;
      }
      this.result = { applied: res.data.applied || 0, rejected: res.data.rejected || [] };
      this.step = 'result';
      this.$emit('applied', this.result);
    }
  }
};
</script>

<style scoped>
.hidden-input {
  display: none;
}
.body-copy {
  color: var(--pw-text-muted);
}
.requirements {
  font-size: 13px;
  color: var(--pw-text-muted);
}
.template-btn {
  margin-left: -8px;
}
.small-copy {
  font-size: 12px;
}
.footnote {
  font-size: 12px;
  color: var(--pw-text-faint);
}
.folios {
  font-size: 11px;
  color: var(--pw-text-muted);
}
</style>
